// Getting in.
//
// This API has no login route, on purpose. `docs/ARCHITECTURE.md` §1 fixes token-based auth
// against an identity vendor, and `packages/auth` verifies a token the vendor minted —
// there is no password anywhere in this system and no endpoint that would take one.
//
// With `VITE_CLERK_PUBLISHABLE_KEY` built in, this screen mounts the vendor's own
// component — Google, Apple, an email code — and `clerk.ts` writes the session it
// establishes into `session.ts`, at which point the router swaps this route away.
//
// TWO MODES, ONE SCREEN. Somebody arriving at Mor with no session is more likely to be new
// than returning — an invitee already has `Invite.tsx`, and everyone else is meeting the
// product for the first time — so `/` and every other route redirect signed-out to
// `/signup`, which mounts the vendor's sign-up. `/signin` stays for the returning person,
// and each card is told where the other one lives (`signInUrl`, `signUpUrl`), so the
// vendor's own "already have an account" and "sign up" links move between these two routes
// instead of the vendor's default `/sign-in` and `/sign-up`, which this app does not serve.
// Both wear `screen--signin`, because `styles.css` dresses the vendor's controls through
// that class and the two cards are the same card.
//
// A build without the key has no way to sign anyone in, so both routes show one line saying
// so, in the same card.

import { useEffect, useRef, useState } from 'react';

import { clerkConfigured, getClerk } from '../clerk.js';
import { vendorAppearance } from '../vendor-appearance.js';

type Mode = 'in' | 'up';

function VendorCard({ mode }: { mode: Mode }): JSX.Element {
  const mount = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = mount.current;
    const vendor = getClerk();
    if (element === null || vendor === null) {
      return undefined;
    }
    let unmounted = false;
    const props = {
      appearance: vendorAppearance(),
      signInUrl: '/signin',
      signUpUrl: '/signup',
    };
    void vendor
      .then((clerk) => {
        if (!unmounted) {
          if (mode === 'up') {
            clerk.mountSignUp(element, props);
          } else {
            clerk.mountSignIn(element, props);
          }
        }
      })
      .catch(() => {
        if (!unmounted) {
          setFailed(true);
        }
      });
    return () => {
      unmounted = true;
      void vendor
        .then((clerk) => {
          if (mode === 'up') {
            clerk.unmountSignUp(element);
          } else {
            clerk.unmountSignIn(element);
          }
        })
        .catch(() => undefined);
    };
  }, [mode]);

  return (
    <main className="screen screen--signin">
      <h1 className="screen__title">{mode === 'up' ? 'Sign up' : 'Sign in'}</h1>
      <div ref={mount} />
      {failed ? (
        <p className="note" role="status">
          {mode === 'up' ? 'Sign-up' : 'Sign-in'} didn’t load. Check the connection and
          reload this page.
        </p>
      ) : null}
    </main>
  );
}

function Entry({ mode }: { mode: Mode }): JSX.Element {
  if (clerkConfigured()) {
    return <VendorCard mode={mode} />;
  }

  return (
    <main className="screen screen--signin">
      <h1 className="screen__title">{mode === 'up' ? 'Sign up' : 'Sign in'}</h1>
      <p className="note" role="status">
        Sign-in isn’t set up for this build.
      </p>
    </main>
  );
}

export function SignIn(): JSX.Element {
  return <Entry mode="in" />;
}

export function SignUp(): JSX.Element {
  return <Entry mode="up" />;
}
